import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, PenLine } from "lucide-react";
import { apiUrl, preconnectApi } from "@/lib/api";
import { trackEvent, getAnonymousId } from "@/lib/tracker";
import { saveCompanyDraft } from "@/lib/needDraft";
import { gaTrack } from "@/lib/analytics";
import { SiftMark } from "@/components/SiftWordmark";
import LocationInput from "@/components/LocationInput";
import StepProgress from "@/components/StepProgress";
import TypedPostCard from "@/components/postings/TypedPostCard";

/* "Post what you need", meaning it.

   The door said post and then rendered the signup form, so a company that
   pressed the thing the page is built around got a registration screen. That is
   the whole demand-side funnel in one interaction: of 253 organizations that
   started signup since 1 July, 40 ever reached a composer and 7 finished. The
   individual side, which lets somebody build their post first and asks for the
   account afterwards, converts at 18% and 61% of those go on to post.

   So this is the individual flow's shape applied to companies. Two ways in,
   because companies arrive in two states: some have a job description already
   written and want to paste it, some have a role in their head and a sentence
   is all they can give. Both end in an editable draft, and the draft is carried
   into signup rather than the visitor being asked to remember it.

   The endpoint is the same public one the talent side uses. It answers a model
   failure with a posting built from the visitor's own words, so a draft always
   comes back and "continue" is never blocked on our infrastructure. */

const QUOTA_REASONS = new Set(["quota", "rate_limited", "daily_cap"]);

/* Who is here, before we ask them for anything.

   A third of the companies who reached this screen left without typing a
   word. They arrive asking whether anybody on Sift is worth hiring, and the
   screen answered with a text box. This answers with the real number of
   people who posted their availability this week and the three latest posts.

   Below ten it says nothing: a small number on a signup screen argues against
   signing up. Title and town only; names are for after there is an account. */
const SUPPLY_FLOOR = 10;

function SupplyStrip() {
  const [supply, setSupply] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(apiUrl("/api/accounts/public/supply-summary/"), {
          credentials: "omit", mode: "cors",
        });
        const data = res.ok ? await res.json() : null;
        if (!alive || !data) return;
        setSupply(data);
        trackEvent("supply_strip_view", {
          account_type: "organization", flow: "company_doors",
          people: data.people_this_week || 0, shown: (data.people_this_week || 0) >= SUPPLY_FLOOR,
        });
      } catch { /* the composer stands on its own */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!supply || (supply.people_this_week || 0) < SUPPLY_FLOOR) return null;
  return (
    <div className="mt-4 rounded-xl border border-line bg-sunken px-3.5 py-3">
      <p className="text-sm text-ink">
        <span className="font-semibold">{supply.people_this_week} people</span> posted their
        availability on Sift this week.
      </p>
      <ul className="mt-1.5 space-y-0.5">
        {(supply.recent || []).map((r, i) => (
          <li key={i} className="text-xs text-secondary min-w-0 break-words">
            {r.title}{r.location ? <span className="text-tertiary"> · {r.location}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function CompanyNeedComposer({ onDone, onBack, totalSteps = 3, step = 1, seed = "", autoDraft = false }) {
  /* Seeded from the scan when there was one: somebody whose job board says
     "Commercial Electrician" should not be asked to type it again. */
  const [raw, setRaw] = useState(seed || "");
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [note, setNote] = useState("");
  // The draft is shown as a post card; the fields are one tap away. `edited`
  // stops the card typing itself out again after they come back from an edit.
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    trackEvent("need_composer_view", { account_type: "organization", flow: "company_doors" });
  }, []);

  // The draft request is the next thing this screen sends; have the
  // connection open before they finish typing. See preconnectApi.
  useEffect(() => preconnectApi(), []);

  const pasted = raw.trim().length > 180;   // long enough to be a real description

  /* From CompanyAsk: they already said what they need, so draft it straight
     away instead of asking a second time. */
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!autoDraft || autoStarted.current || (seed || "").trim().length < 3) return;
    autoStarted.current = true;
    makeDraft();
  }, [autoDraft, seed]); // eslint-disable-line react-hooks/exhaustive-deps

  const makeDraft = async () => {
    const text = raw.trim();
    if (text.length < 3) {
      // An empty box is an answer, not a mistake. Send them on rather than
      // telling them off; the predecessor's refusal produced dead taps.
      trackEvent("get_started_skip_post", { account_type: "organization", flow: "company_doors", reason: "empty" });
      return onDone(null);
    }
    setDrafting(true);
    setNote("");
    trackEvent("need_draft_requested", {
      account_type: "organization", flow: "company_doors",
      source: pasted ? "pasted_description" : "role_sentence",
    });
    try {
      const res = await fetch(apiUrl("/api/accounts/public/need/suggest-posting/"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        // The endpoint caps `need` at its own length; sending the whole pasted
        // description rather than a truncated title is the point of the paste
        // path, so it gets more room than the one-line path needs.
        body: JSON.stringify({
          need: text.slice(0, 4000),
          poster_type: "org",
          anonymous_id: getAnonymousId(),
        }),
        credentials: "omit",
        mode: "cors",
      });
      const data = res.ok ? await res.json().catch(() => ({})) : {};
      if (data?.available && data?.draft?.title) {
        setDraft({
          title: data.draft.title || "",
          description: data.draft.description || "",
          location: data.draft.location || "",
        });
        trackEvent("need_draft_ready", {
          account_type: "organization", flow: "company_doors",
          drafted_by: data.source || "model",
        });
      } else {
        // Silent to them, loud to us. Their words are still on the screen and
        // there is nothing they can do about our model.
        trackEvent("need_draft_failed", {
          account_type: "organization", flow: "company_doors",
          reason: data?.reason || (res.ok ? "empty_response" : `http_${res.status}`),
        });
        setDraft({ title: text.slice(0, 120), description: pasted ? text : "", location: "" });
      }
      if (QUOTA_REASONS.has(data?.reason)) {
        setNote("Drafting is busy right now, so this one is built from your own words. Edit it however you like.");
      }
    } catch {
      trackEvent("need_draft_failed", { account_type: "organization", flow: "company_doors", reason: "network" });
      setDraft({ title: text.slice(0, 120), description: pasted ? text : "", location: "" });
    } finally {
      setDrafting(false);
    }
  };

  /* Who already matches what we just drafted.
     Same endpoint and same matcher the scan arm uses, asked about one role
     instead of a company's whole board. Runs after the draft is on screen and
     never blocks it: if it finds nobody, or fails, the screen is exactly what
     it was before. */
  const [matches, setMatches] = useState(null);
  useEffect(() => {
    if (!draft?.title) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(apiUrl("/api/accounts/public/company/scan/"), {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            role: draft.title, description: draft.description || "",
            location: draft.location || "", anonymous_id: getAnonymousId(),
          }),
          credentials: "omit", mode: "cors",
        });
        const data = res.ok ? await res.json().catch(() => null) : null;
        if (!alive) return;
        /* A zero is an answer, and it used to be dropped on the floor: this
           returned early unless somebody matched, so `matches` stayed null and
           the screen could not tell "nobody fits" from "still asking". The
           screen needs to know, because a company who is told nothing has to
           guess, and the guess is that they are wasting their time. */
        setMatches(data || { matched: false, match_count: 0 });
        trackEvent("draft_matches_shown", {
          account_type: "organization", flow: "company_doors",
          count: data?.match_count || 0, matched: !!data?.matched,
        });
      } catch { /* the draft stands on its own */ }
    })();
    return () => { alive = false; };
  }, [draft?.title, draft?.description, draft?.location]);

  const cont = () => {
    trackEvent("need_details_completed", { account_type: "organization", flow: "company_doors" });
    gaTrack("need_details_completed", { account_type: "organization" });
    /* Carry the count to the account screen. It knew how many people matched
       and then handed on the draft alone, so the next screen changed the
       subject back to the post while the reader was still thinking about the
       four faces it had just shown them. */
    const payload = { ...draft, matchCount: matches?.matched ? (matches.match_count || 0) : 0 };
    // Survive a dismissal. See saveCompanyDraft: closing the hero panel used to
    // destroy the post they had just written.
    saveCompanyDraft(payload);
    onDone(payload);
  };

  const field = "w-full rounded-xl border border-line-control bg-surface px-3 py-2.5 text-ink " +
                "focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand";

  return (
    <div
      className="min-h-screen bg-bg flex items-start sm:items-center justify-center px-4 pb-[40vh] sm:pb-16"
      style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 2rem)" }}
    >
      <div className="relative w-full max-w-lg bg-surface border border-line rounded-2xl shadow-sift-sm p-7">
        {/* Back sits in the card's top-left corner (Jake, 2026-10-04) and goes
            one step back: to the question they answered. */}
        {!drafting && (
          <button type="button" onClick={onBack}
                  className="absolute top-4 left-4 inline-flex items-center gap-1 min-h-[36px] px-1 text-xs text-secondary hover:text-ink">
            <ArrowLeft size={13} strokeWidth={2.2} aria-hidden="true" /> Back
          </button>
        )}
        <div className="flex justify-center mb-5"><SiftMark size={44} /></div>
        <StepProgress step={draft || drafting ? step + 1 : step} total={totalSteps} />

        {!draft && !drafting ? (
          <>
            {/* An open question and an empty box: no example text, so what
                they write is theirs and tells us what people come here for. */}
            <h1 className="font-display" style={{ fontSize: "clamp(1.375rem,4.6vw,1.8125rem)", lineHeight: 1.15, margin: 0, color: "var(--ink)" }}>
              Tell us what you&rsquo;re looking for
            </h1>

            <SupplyStrip />
            <textarea
              ref={box}
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              rows={6}
              aria-label="Tell us what you're looking for"
              className={`${field} mt-4 resize-y`}
            />

            <button
              type="button"
              onClick={makeDraft}
              disabled={drafting}
              className="mt-4 w-full min-h-[48px] rounded-pill bg-brand text-white font-semibold
                         flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {drafting ? "Writing it..." : (
                <>
                  <PenLine size={17} strokeWidth={2.2} aria-hidden="true" />
                  {pasted ? "Tidy this into a post" : "Write the post for me"}
                </>
              )}
            </button>

            {/* No "Skip and create account" or "Actually, I'm looking for work"
                here (Jake, 2026-10-04): the doors screen one step back offers
                both, and Back returns to it. This screen asks one thing. */}
          </>
        ) : (
          <>
            <h1 className="font-display" style={{ fontSize: "clamp(1.375rem,4.6vw,1.8125rem)", lineHeight: 1.15, margin: 0, color: "var(--ink)" }}>
              {autoDraft ? "Here's your post, ready to find them." : "Here's what it's going to look like."}
            </h1>
            <p className="mt-2 text-sm text-secondary">
              Change anything. It posts free once your account exists.
            </p>
            {note && <p className="mt-3 text-sm text-tertiary">{note}</p>}

            {/* The post as the board will show it, typed out, rather than a
                form. Half the companies who asked for a draft left on this
                screen when it was three boxes to edit: a form reads as more
                work, a finished post reads as work done for you. The card is
                up the moment they ask, so the wait for the model is spent
                watching it start rather than watching a button. */}
            {!editing ? (
              <div className="mt-4">
                <TypedPostCard
                  location={draft?.location || ""}
                  post={draft ? { title: draft.title, description: draft.description } : undefined}
                  instant={edited}
                />
                {draft && (
                  <button
                    type="button"
                    onClick={() => {
                      trackEvent("draft_preview_edit", { account_type: "organization", flow: "company_doors" });
                      setEditing(true);
                    }}
                    className="mt-2 text-sm text-secondary underline hover:text-ink"
                  >
                    Edit the post
                  </button>
                )}
              </div>
            ) : (
              <>
                <label htmlFor="cnc-title" className="block mt-4 text-sm font-semibold text-ink">Role</label>
                <input id="cnc-title" aria-label="Role" className={`${field} mt-1`} value={draft.title}
                       onChange={(e) => setDraft({ ...draft, title: e.target.value })} />

                <label htmlFor="cnc-desc" className="block mt-3 text-sm font-semibold text-ink">Details</label>
                <textarea id="cnc-desc" aria-label="Details" className={`${field} mt-1 resize-y`} rows={7} value={draft.description}
                          onChange={(e) => setDraft({ ...draft, description: e.target.value })} />

                <button
                  type="button"
                  onClick={() => { setEdited(true); setEditing(false); }}
                  className="mt-2 text-sm text-secondary underline hover:text-ink"
                >
                  Done editing
                </button>
              </>
            )}

            {draft && (
              <>
                <label htmlFor="cnc-loc" className="block mt-4 text-sm font-semibold text-ink">Location</label>
                {/* Kept out of the edit panel on purpose. Location is one of
                    the signals the matcher weights and it is compared with an
                    ILIKE, so "Indy" and "indianapolis in" match nobody; behind
                    an Edit link it would be left blank. */}
                <div className="mt-1">
                  <LocationInput
                    id="cnc-loc"
                    value={draft.location}
                    onChange={(v) => setDraft({ ...draft, location: v })}
                    className={field}
                  />
                </div>
              </>
            )}

            {matches?.matched && (
              <div className="mt-5">
                {/* Only ever states a number we have. When nothing matches this
                    block does not render at all, so the screen never promises
                    matching it cannot deliver. */}
                <p className="text-sm font-semibold text-ink">
                  We have {matches.match_count}{" "}
                  {matches.match_count === 1 ? "match" : "matches"} for this draft as it is.
                </p>
                <p className="mt-2 rounded-xl border border-brand/30 bg-brand-tint/50 px-4 py-3 text-sm text-ink">
                  Hire on contract. If it is a great long term fit, you can take them on
                  permanently without any additional fees, ever.
                </p>
                <div className="mt-3 grid gap-2.5">
                  {(matches.cards || []).slice(0, 4).map((c, i) => (
                    <div key={i} className="flex items-start gap-3 rounded-xl border border-line bg-surface p-3">
                      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full
                                       bg-brand-tint text-brand-ink font-bold">
                        <span className="blur-[5px] select-none" aria-hidden="true">{c.initials}</span>
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-ink min-w-0 break-words">{c.headline}</span>
                        <span className="block text-xs text-secondary">{c.location || "Location on request"}</span>
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-faint">Names unlock when your account exists.</p>
              </div>
            )}

            {/* Nobody fits yet, and the post is still worth making.
                This screen used to render nothing at all in that case: the
                match block was gated on `matched`, so a company whose role had
                no takers saw their draft, a button, and silence, and had to
                work out for themselves whether any of it had been worth doing.

                What replaces it is true rather than encouraging. A posting is
                matched again every time somebody new posts availability, which
                is the actual behaviour -- the cross-matcher runs on each new
                posting, not once at creation -- so a post made today keeps
                working, and the org is emailed when a match clears the notify
                floor. "Every day" is the conservative reading of the board:
                43 availability posts a day over the last week, 19 a day over
                the slowest four-week stretch. No count is printed, because a
                number in shipped copy goes stale and this one is climbing. */}
            {matches && !matches.matched && (
              <div className="mt-5 rounded-xl border border-line bg-surface p-4">
                <p className="text-sm font-semibold text-ink">
                  No one on the board fits this yet. Post it anyway.
                </p>
                <p className="mt-2 text-sm text-secondary">
                  Your post is matched against every person who joins, not just the
                  ones here today. People post their availability every day, and
                  we'll email you the moment one of them fits this.
                </p>
                <p className="mt-2.5 text-xs text-faint">
                  It also goes on the public board straight away, where people search
                  and apply directly.
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={cont}
              disabled={!draft?.title?.trim()}
              className="mt-5 w-full min-h-[48px] rounded-pill bg-brand text-white font-semibold
                         flex items-center justify-center gap-2 disabled:opacity-60"
            >
              Finish account <ArrowRight size={17} strokeWidth={2.2} aria-hidden="true" />
            </button>
            {/* "Great!" went: it was the second of two on consecutive screens,
                and it reads as forced congratulation to somebody who has just
                been told nobody matches them yet. */}
            <p className="mt-2 text-center text-xs text-tertiary">
              Your post goes live as soon as your account exists.
            </p>

          </>
        )}
      </div>
    </div>
  );
}
